import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { supabase } from './lib/supabase';
import { useAuthStore } from './store/authStore';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import MainLayout from './components/layout/MainLayout';
import { CoachFeatureGate } from './components/layout/CoachFeatureGate';
import TeamSetup from './components/TeamSetup';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import DeleteAccountPage from './pages/DeleteAccountPage';
import Dashboard from './components/Dashboard';
import TeamHub from './components/TeamHub';
import RosterManagement from './components/PlayerManagement';
import PracticeManagement from './components/PracticeManagement';
import PlaybookLibrary from './components/PlaybookLibrary';
import GamePlanManagement from './components/gameplan/GamePlanManagement';
import DepthChart from './components/DepthChart';

import Settings from './components/Settings';
import Logo from './components/layout/Logo';
import TestAI from './components/TestAI';
import AuthCallback from './pages/AuthCallback';
import SplashPage from './components/SplashPage';
import OnboardingTooltips from './components/OnboardingTooltips';
import ErrorBoundary from './components/ErrorBoundary';
import { InstallPrompt } from './components/pwa/InstallPrompt';
import { SubstitutionReport } from './components/gameplan/SubstitutionReport';
import { TeamAttendanceReport } from './components/attendance/TeamAttendanceReport';
import { PlayerCardList } from './components/player-cards/PlayerCardList';
import { PlayerCardDetail } from './components/player-cards/PlayerCardDetail';
import { PlayerCardForm } from './components/player-cards/PlayerCardForm';
import { GamesManagement } from './components/GamesManagement';
import { GameDetailView } from './components/games/GameDetailView';
import { TennisScoringScreen } from './components/games/tennis/scoring/TennisScoringScreen';
import LegalIndexPage from './pages/legal/LegalIndexPage';
import LegalDocPage from './pages/legal/LegalDocPage';
import SharePractice from './pages/SharePractice';
import ShareLineup from './pages/ShareLineup';
import Welcome from './pages/Welcome';
import NotFoundPage from './pages/NotFoundPage';

const App: React.FC = () => {
  const { user, setUser, setSession, setIsAuthenticated, refreshSession } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

  useEffect(() => {
    // Check if onboarding has been completed
    const onboardingComplete = localStorage.getItem('onboardingComplete');
    setHasCompletedOnboarding(!!onboardingComplete);
  }, []);

  useEffect(() => {
    const checkAuthState = async () => {
      try {
        await refreshSession();
      } catch (error) {
        console.error('Error checking auth state:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_OUT') {
          setIsAuthenticated(false);
          setUser(null);
          setSession(null);
        } else if (event === 'PASSWORD_RECOVERY') {
          // Don't log the user in — route to the reset form instead.
          // This also serves as a fallback when the Supabase redirectTo URL
          // isn't in the allowlist and the user lands on a different page.
          if (!window.location.pathname.includes('/reset-password')) {
            window.location.href = '/reset-password';
          }
        } else if (session) {
          setIsAuthenticated(true);
          setUser(session.user);
          setSession(session);
        }
      }
    );

    checkAuthState();

    let urlListener: { remove: () => void } | undefined;
    let browserFinishedListener: { remove: () => void } | undefined;

    if (Capacitor.isNativePlatform()) {
      // appUrlOpen fires when Android routes the com.hustli.app:// deep link back
      // to the app after the OAuth browser redirects to our custom scheme.
      CapacitorApp.addListener('appUrlOpen', async ({ url }) => {

        if (url.startsWith('com.hustli.app://')) {
          await Browser.close();
          const fragment = url.split('#')[1] ?? '';
          const params = new URLSearchParams(fragment);
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');
          if (accessToken && refreshToken) {
            const { error } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
            if (error) {
              toast.error(`Sign in failed: ${error.message}`);
              console.error('[App] setSession failed:', error);
            }
          } else {
            // Fallback: try PKCE code in query string
            const code = url.match(/[?&]code=([^&#\s]+)/)?.[1];
            if (code) {
              const { error } = await supabase.auth.exchangeCodeForSession(code);
              if (error) {
                toast.error(`Sign in failed: ${error.message}`);
                console.error('[App] exchangeCodeForSession failed:', error);
              }
            } else {
              toast.error('Sign in failed: unrecognised callback URL');
              console.error('[App] No tokens or code in OAuth callback URL:', url);
            }
          }
        }
      }).then(l => { urlListener = l; });

      Browser.addListener('browserFinished', () => {
        console.log('[App] browserFinished');
      }).then(l => { browserFinishedListener = l; });
    }

    return () => {
      subscription.unsubscribe();
      urlListener?.remove();
      browserFinishedListener?.remove();
    };
  }, [setIsAuthenticated, setUser, setSession, refreshSession]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-cream">
        <Logo className="h-20 mb-6" />
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="text-stone mt-4">Loading...</p>
      </div>
    );
  }

  return (
    <Router>
      <Toaster 
        position="top-right" 
        toastOptions={{ 
          duration: 5000,
          style: {
            background: '#F7F5F0',
            color: '#3A3C3E',
            border: '1px solid #4DBAAC',
            maxWidth: '100%',
            width: 'auto',
            margin: '0.5rem',
            padding: '1rem',
            borderRadius: '0.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        }} 
      />
        <Routes>
          <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
          <Route path="/register" element={!user ? <Register /> : <Navigate to="/" />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Public legal pages */}
          <Route path="/legal" element={<LegalIndexPage />} />
          <Route path="/legal/:slug" element={<LegalDocPage />} />

          {/* Public share pages */}
          <Route path="/share/practice/:token" element={<SharePractice />} />
          <Route path="/share/lineup/:token" element={<ShareLineup />} />
          
          {/* Public landing page */}
          <Route path="/" element={!user ? <SplashPage /> : <Navigate to="/dashboard" />} />
          
          {/* Protected routes that require authentication */}
          <Route
            path="/dashboard"
            element={
              user ? (
                <MainLayout>
                  <Dashboard />
                  {!hasCompletedOnboarding && <OnboardingTooltips />}
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/team/:teamId"
            element={
              user ? (
                <MainLayout>
                  <TeamHub />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/welcome"
            element={
              user ? (
                <MainLayout>
                  <Welcome />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/team-setup"
            element={
              user ? (
                <MainLayout>
                  <TeamSetup />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/players"
            element={
              user ? (
                <MainLayout>
                  <CoachFeatureGate>
                    <RosterManagement />
                  </CoachFeatureGate>
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/depth-chart"
            element={
              user ? (
                <MainLayout>
                  <CoachFeatureGate>
                    <DepthChart />
                  </CoachFeatureGate>
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/practices"
            element={
              user ? (
                <MainLayout>
                  <ErrorBoundary>
                    <PracticeManagement />
                  </ErrorBoundary>
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/playbook"
            element={
              user ? (
                <MainLayout>
                  <PlaybookLibrary />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/game-plans"
            element={
              user ? (
            <MainLayout>
                  <ErrorBoundary>
              <GamePlanManagement />
                  </ErrorBoundary>
            </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route path="/settings" element={
            <MainLayout>
              <Settings />
            </MainLayout>
          } />
          {/* Coach Steve is now an off-canvas panel from the FAB; redirect old URL to dashboard */}
          <Route path="/coach-steve" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/games"
            element={
              user ? (
                <MainLayout>
                  <GamesManagement />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/games/:gameId"
            element={
              user ? (
                <MainLayout>
                  <GameDetailView />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          {/* Full-screen live scoring pad — no MainLayout chrome, mobile-first (sc-338). */}
          <Route
            path="/games/:gameId/score"
            element={user ? <TennisScoringScreen /> : <Navigate to="/login" />}
          />
          <Route
            path="/player-cards"
            element={
              user ? (
                <MainLayout>
                  <PlayerCardList />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/player-cards/:playerId"
            element={
              user ? (
                <MainLayout>
                  <PlayerCardDetail />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/player-cards/:playerId/edit"
            element={
              user ? (
                <MainLayout>
                  <PlayerCardForm />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/player-cards/new"
            element={
              user ? (
                <MainLayout>
                  <PlayerCardForm />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route path="/test-ai" element={
            <MainLayout>
              <TestAI />
            </MainLayout>
          } />
          <Route
            path="/teams/:teamId/substitution-report"
            element={<SubstitutionReportWrapper />}
          />
          <Route
            path="/teams/:teamId/attendance-report"
            element={
              user ? (
                <MainLayout>
                  <TeamAttendanceReport />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <InstallPrompt />
    </Router>
  );
};

const SubstitutionReportWrapper = () => {
  const { teamId } = useParams();
  return <SubstitutionReport teamId={teamId || ''} />;
};

export default App;